// ./src/components/features/EventPartners/EventPartnersFilterBar/EventPartnersFilterBar.tsx
//
// Filter controls for the event partners list: status (visible/hidden), partner
// type (multi), money direction. Controlled — the page maps it to an
// EventPartnerFilter.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  PARTNER_KIND_VALUES,
  PARTNER_MONEY_DIRECTION_VALUES,
  PARTNER_KIND_LABELS,
  PARTNER_MONEY_DIRECTION_LABELS,
} from "@/utils/api";
import { AppIcon, MultiSelectField } from "@/components/generic";

export type PartnerStatusFilter = "visible" | "hidden";
export type PartnerMoneyFilter = "revenue" | "expense" | "none";

export interface EventPartnersFilterValue {
  status: PartnerStatusFilter | null;
  partnerKinds: string[];
  moneyDirection: PartnerMoneyFilter | null;
}

export interface EventPartnersFilterBarProps {
  value: EventPartnersFilterValue;
  onChange: (value: EventPartnersFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_EVENT_PARTNERS_FILTER: EventPartnersFilterValue = {
  status: null,
  partnerKinds: [],
  moneyDirection: null,
};

export const isEventPartnersFilterActive = (v: EventPartnersFilterValue): boolean =>
  Boolean(v.status) || v.partnerKinds.length > 0 || Boolean(v.moneyDirection);

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 220px", minWidth: 200 };
const KIND_STYLE: React.CSSProperties = { flex: "1 1 260px", minWidth: 240 };
const MONEY_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };

const ANY = "any";

const EventPartnersFilterBarComponent: React.FC<EventPartnersFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["partners", "common"]);

  const statusOptions = [
    { value: ANY, label: t("partners:filters.status.any", { defaultValue: "Any" }) },
    { value: "visible", label: t("partners:filters.status.visible", { defaultValue: "Visible" }) },
    { value: "hidden", label: t("partners:filters.status.hidden", { defaultValue: "Hidden" }) },
  ];

  const moneyOptions = [
    { value: ANY, label: t("partners:filters.money.any", { defaultValue: "Any" }) },
    ...PARTNER_MONEY_DIRECTION_VALUES.map((v) => ({
      value: v,
      label: t(`partners:money.${v}`, { defaultValue: PARTNER_MONEY_DIRECTION_LABELS[v] }),
    })),
  ];

  const kindOptions = PARTNER_KIND_VALUES.map((v) => ({
    value: v,
    label: t(`partners:kind.${v}`, { defaultValue: PARTNER_KIND_LABELS[v] }),
  }));

  const active = isEventPartnersFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("partners:filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) => onChange({ ...value, status: next === ANY ? null : (next as PartnerStatusFilter) })}
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={KIND_STYLE}>
        <MultiSelectField
          label={t("partners:filters.kind.label", { defaultValue: "Partner type" })}
          placeholder={value.partnerKinds.length > 0 ? undefined : t("partners:filters.kind.placeholder", { defaultValue: "Any type" })}
          data={kindOptions}
          value={value.partnerKinds}
          onChange={(_name, next) => onChange({ ...value, partnerKinds: next })}
          disabled={disabled}
          clearable
          searchable
          size="md"
        />
      </Box>

      <Box style={MONEY_STYLE}>
        <Input.Wrapper label={t("partners:filters.money.label", { defaultValue: "Money" })}>
          <SegmentedControl
            data={moneyOptions}
            value={value.moneyDirection ?? ANY}
            onChange={(next) => onChange({ ...value, moneyDirection: next === ANY ? null : (next as PartnerMoneyFilter) })}
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_EVENT_PARTNERS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const EventPartnersFilterBar = React.memo(EventPartnersFilterBarComponent);
